'use client';

import * as React from 'react';
import { useToggle } from '@hooks';

import { CookiePreferencesModal } from './cookie-preferences-modal';
import {
  CookieConsentPreferences,
  getStoredConsentPreferences,
  saveConsentPreferences,
} from './utils';

interface CookieSettingsButtonProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * A button/link wrapper that opens the cookie preferences modal when clicked.
 * Use this to allow users to modify their cookie settings after initial consent.
 *
 * @example
 * ```tsx
 * <CookieSettingsButton className="text-sm hover:underline">
 *   Cookie Settings
 * </CookieSettingsButton>
 * ```
 */
export const CookieSettingsButton: React.FC<CookieSettingsButtonProps> = ({
  children,
  className,
}) => {
  const { on: isModalOpen, setOn: openModal, setOff: closeModal } = useToggle();
  const [storedPreferences, setStoredPreferences] =
    React.useState<CookieConsentPreferences | null>(null);

  // Load current preferences when modal opens
  const handleOpenModal = (): void => {
    const preferences = getStoredConsentPreferences();
    setStoredPreferences(preferences);
    openModal();
  };

  const handleSavePreferences = (
    preferences: Omit<
      CookieConsentPreferences,
      'consentDate' | 'consentVersion'
    >
  ): void => {
    saveConsentPreferences(preferences);
    // Dispatch event so other parts of the app can react to preference changes
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('cookieConsentUpdated', { detail: preferences })
      );
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpenModal}
        className={className}
        aria-label="Open cookie settings"
      >
        {children}
      </button>

      <CookiePreferencesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSavePreferences}
        initialPreferences={storedPreferences}
      />
    </>
  );
};

CookieSettingsButton.displayName = 'CookieSettingsButton';
